import type { Edge, Node } from '@xyflow/react'
import { getSubnetDescendants } from '@/features/backtest/utils/collapse-model'
import { getLowestTimeframePriceNode } from '@/features/backtest/utils/price-data-ownership'

/**
 * The spine: every strategy keeps a Stock Selection, a main Price Data, a
 * Trade Manager and Run Settings. Extra copies delete normally; the last one (the
 * `isPrimary` Price Data, else the one the run reads) never does. Nothing is
 * added on open: a saved strategy missing one is left as it is.
 */
const SPINE_KINDS = [
  { type: 'universe', name: 'Stock Selection' },
  { type: 'priceData', name: 'Price Data' },
  { type: 'tradeManager', name: 'Trade Manager' },
  { type: 'report', name: 'Run Settings' },
] as const

const isContainer = (node: Node | undefined) => node?.type === 'subnet' || node?.type === 'group'

/** Ids of the spine nodes that must survive, one entry per kind at most. */
function protectedIds(nodes: Node[], gone: Set<string>): { id: string; name: string }[] {
  const kept: { id: string; name: string }[] = []
  for (const { type, name } of SPINE_KINDS) {
    const copies = nodes.filter((node) => node.type === type)
    if (copies.length === 0) continue
    // Price Data protects the MAIN one even beside other copies (a second
    // timeframe is not the one the run reads); the other kinds protect the
    // last copy, the first in graph order when every copy is going.
    const survivor =
      type === 'priceData'
        ? (copies.find((node) => node.data?.isPrimary === true) ??
          getLowestTimeframePriceNode(copies) ??
          copies[0])
        : copies.some((node) => !gone.has(node.id))
          ? null
          : copies[0]
    if (!survivor || !gone.has(survivor.id)) continue
    kept.push({ id: survivor.id, name })
  }
  return kept
}

export interface SpineDeleteInput {
  nodes: Node[]
  edges: Edge[]
  /** Node ids about to be deleted. */
  nodeIds: string[]
  /** Wire ids about to be deleted, if any. */
  edgeIds?: string[]
}

export interface SpineDeleteResult {
  /** The node ids that may go. */
  nodeIds: string[]
  /** The wire ids that may go. */
  edgeIds: string[]
  /** Display names of the spine kinds that stayed; empty when nothing was refused. */
  keptNames: string[]
}

export function guardSpineDelete({
  nodes,
  edges,
  nodeIds,
  edgeIds = [],
}: SpineDeleteInput): SpineDeleteResult {
  // A container takes its descendants with it, so they count as going too.
  const gone = new Set(nodeIds)
  for (const id of nodeIds) {
    const node = nodes.find((x) => x.id === id)
    if (isContainer(node)) getSubnetDescendants(id, nodes).forEach((d) => gone.add(d))
  }

  const spine = protectedIds(nodes, gone)
  if (spine.length === 0) return { nodeIds, edgeIds, keptNames: [] }

  // A container holding a spine node stays whole, with its ancestors. Moving
  // the spine node out would silently re-parent it; keeping the frame is the
  // smaller surprise, and the rest of its contents stay with it.
  const keep = new Set<string>()
  const byId = new Map(nodes.map((node) => [node.id, node]))
  for (const { id } of spine) {
    keep.add(id)
    for (let p = byId.get(id)?.parentId; p && !keep.has(p); p = byId.get(p)?.parentId) keep.add(p)
  }

  // React Flow lists a deleted container's descendants as selected too, so a
  // kept container keeps its whole subtree rather than being emptied.
  for (const id of [...keep]) {
    if (nodeIds.includes(id) && isContainer(byId.get(id))) {
      getSubnetDescendants(id, nodes).forEach((d) => keep.add(d))
    }
  }

  const remaining = nodeIds.filter((id) => !keep.has(id))
  const removed = new Set(remaining)
  // A wire that touches a kept node is only taken when the node on its other
  // end really goes (it is then bypassed or cut with that node).
  const edgeById = new Map(edges.map((edge) => [edge.id, edge]))
  const remainingEdges = edgeIds.filter((id) => {
    const edge = edgeById.get(id)
    if (!edge) return true
    const touchesKept = keep.has(edge.source) || keep.has(edge.target)
    return !touchesKept || removed.has(edge.source) || removed.has(edge.target)
  })

  return { nodeIds: remaining, edgeIds: remainingEdges, keptNames: spine.map((s) => s.name) }
}

function joinNames(names: string[]): string {
  if (names.length <= 1) return names.join('')
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
}

export function spineKeptMessage(names: string[]): string {
  return `Every strategy keeps a Stock Selection, a main Price Data, a Trade Manager and Run Settings, so ${joinNames(names)} stayed.`
}
